import { Link } from "react-router-dom";
import { useIntl } from "react-intl";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import {
  ErrorOutline,
  Home,
  Refresh,
  Schedule,
} from "@mui/icons-material";
import { PAGE_STATUS } from "../constants";

function StateCard({
  dir,
  icon,
  titleId,
  titleDefault,
  bodyId,
  bodyDefault,
  showRetry,
  onRetry,
  tone = "info",
}) {
  const intl = useIntl();

  return (
    <Box
      className={`shared-chat-state-card shared-chat-state-card--${tone}`}
      role="status"
    >
      <Box className="shared-chat-state-icon" aria-hidden>
        {icon}
      </Box>
      <Typography className="shared-chat-state-title">
        {intl.formatMessage({ id: titleId, defaultMessage: titleDefault })}
      </Typography>
      <Typography className="shared-chat-state-body">
        {intl.formatMessage({ id: bodyId, defaultMessage: bodyDefault })}
      </Typography>
      <Box className="shared-chat-state-actions">
        {showRetry && (
          <Button
            variant="contained"
            disableElevation
            className="shared-chat-primary-btn"
            startIcon={<Refresh />}
            onClick={onRetry}
          >
            {intl.formatMessage({
              id: "chatbot.shareRetry",
              defaultMessage: dir === "rtl" ? "إعادة المحاولة" : "Try again",
            })}
          </Button>
        )}
        <Button
          component={Link}
          to="/home"
          variant="outlined"
          className="shared-chat-secondary-btn"
          startIcon={<Home />}
        >
          {intl.formatMessage({
            id: "chatbot.sharedGoHome",
            defaultMessage: dir === "rtl" ? "العودة للرئيسية" : "Go to home",
          })}
        </Button>
      </Box>
    </Box>
  );
}

function SharedChatLoading({ dir }) {
  const intl = useIntl();

  return (
    <Box className="shared-chat-loading" role="status" aria-live="polite">
      <Box className="shared-chat-loading-ring">
        <CircularProgress
          size={42}
          thickness={3.6}
          sx={{ color: "var(--main-color, #0986ed)" }}
        />
      </Box>
      <Typography className="shared-chat-loading-text">
        {intl.formatMessage({
          id: "chatbot.sharedLoading",
          defaultMessage:
            dir === "rtl"
              ? "جاري تحميل المحادثة المشتركة..."
              : "Loading shared conversation...",
        })}
      </Typography>
      <Typography className="shared-chat-loading-sub">
        {intl.formatMessage({
          id: "chatbot.sharedLoadingHint",
          defaultMessage:
            dir === "rtl"
              ? "يتم جلب اللقطة الآمنة من الخادم"
              : "Fetching a secure snapshot from the server",
        })}
      </Typography>
    </Box>
  );
}

export default function SharedChatStateCard({ status, dir, onRetry }) {
  if (status === PAGE_STATUS.LOADING) {
    return <SharedChatLoading dir={dir} />;
  }

  if (status === PAGE_STATUS.UNAVAILABLE) {
    return (
      <StateCard
        dir={dir}
        icon={<ErrorOutline />}
        titleId="chatbot.sharedUnavailableTitle"
        titleDefault={
          dir === "rtl" ? "المحادثة غير متاحة" : "Shared chat unavailable"
        }
        bodyId="chatbot.sharedUnavailable"
        bodyDefault={
          dir === "rtl"
            ? "هذه المحادثة المشتركة لم تعد متاحة."
            : "This shared chat is no longer available."
        }
        showRetry={false}
        tone="warn"
      />
    );
  }

  if (status === PAGE_STATUS.EXPIRED) {
    return (
      <StateCard
        dir={dir}
        icon={<Schedule />}
        titleId="chatbot.sharedExpiredTitle"
        titleDefault={dir === "rtl" ? "انتهت صلاحية الرابط" : "Link expired"}
        bodyId="chatbot.sharedExpired"
        bodyDefault={
          dir === "rtl"
            ? "انتهت صلاحية رابط المشاركة ولم يعد بالإمكان عرض هذه المحادثة."
            : "This share link has expired and the conversation can no longer be viewed."
        }
        showRetry={false}
        tone="warn"
      />
    );
  }

  if (status === PAGE_STATUS.ERROR) {
    return (
      <StateCard
        dir={dir}
        icon={<ErrorOutline />}
        titleId="chatbot.sharedErrorTitle"
        titleDefault={dir === "rtl" ? "حدث خطأ" : "Something went wrong"}
        bodyId="chatbot.sharedError"
        bodyDefault={
          dir === "rtl"
            ? "حدث خطأ ما. يرجى المحاولة مرة أخرى."
            : "Something went wrong. Please try again."
        }
        showRetry
        onRetry={onRetry}
        tone="error"
      />
    );
  }

  return null;
}
